import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { FileText, FolderOpen, Printer, Receipt, ScrollText, type LucideIcon } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { printDocument } from '@/lib/print';
import { Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ShellPanel, ShellSectionHeading } from '@/components/clinical-shell/ShellPanel';
import { admissionIsOpen, type Admission } from '../types';

export type AdmissionPrint = 'bill' | 'summary';

export function useAdmissionPrints(admissionId: string) {
  const toast = useToast();
  const printDoc = useMutation({
    mutationFn: (kind: AdmissionPrint) =>
      get<{ html: string }>(kind === 'bill' ? `/ipd/admissions/${admissionId}/bill/print` : `/ipd/admissions/${admissionId}/discharge-summary/print`),
    onSuccess: (doc) => printDocument(doc.html),
    onError: (error) => toast.error(tr('ipd', 'could_not_print'), describeError(error)),
  });
  const deathCertificate = useMutation({
    mutationFn: async () => {
      const certificate = await post<{ id: string }>('/mortality/certificate', { admissionId });
      return get<{ html: string }>(`/certificates/${certificate.id}/print`);
    },
    onSuccess: (doc) => printDocument(doc.html),
    onError: (error) => toast.error(tr('ipd', 'could_not_issue_the_death_certificate'), describeError(error)),
  });
  return { printDoc, deathCertificate };
}

function DocumentRow({ icon: Icon, title, hint, action }: { icon: LucideIcon; title: string; hint: string; action: ReactNode }) {
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2.5">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground" aria-hidden>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      {action}
    </li>
  );
}

export function DocumentsSection({ admission }: { admission: Admission }) {
  const { can } = useAuth();
  const { printDoc, deathCertificate } = useAdmissionPrints(admission.id);
  const current = admissionIsOpen(admission);

  return (
    <>
      <ShellSectionHeading icon={ScrollText} title={tr('ipd', 'ws_nav_documents')} description={tr('ipd', 'ws_documents_desc')} />
      <ShellPanel icon={Printer} title={tr('ipd', 'ws_prints')}>
        <ul className="space-y-2">
          <DocumentRow
            icon={Receipt}
            title={current ? tr('ipd', 'ws_print_interim_bill') : tr('ipd', 'ws_print_final_bill')}
            hint={tr('ipd', 'ws_bill_print_hint')}
            action={
              <Button size="sm" variant="outline" onClick={() => printDoc.mutate('bill')} loading={printDoc.isPending && printDoc.variables === 'bill'}>
                <Printer className="h-4 w-4" aria-hidden /> {tr('ipd', 'print')}
              </Button>
            }
          />
          {!current && (
            <DocumentRow
              icon={FileText}
              title={tr('ipd', 'discharge_summary')}
              hint={tr('ipd', 'ws_summary_print_hint')}
              action={
                <Button size="sm" variant="outline" onClick={() => printDoc.mutate('summary')} loading={printDoc.isPending && printDoc.variables === 'summary'}>
                  <Printer className="h-4 w-4" aria-hidden /> {tr('ipd', 'print')}
                </Button>
              }
            />
          )}
          {admission.status === 'expired' && can('certificate.issue') && (
            <DocumentRow
              icon={ScrollText}
              title={tr('ipd', 'death_certificate')}
              hint={tr('ipd', 'ws_death_certificate_hint')}
              action={
                <Button size="sm" variant="outline" onClick={() => deathCertificate.mutate()} loading={deathCertificate.isPending}>
                  <Printer className="h-4 w-4" aria-hidden /> {tr('ipd', 'print')}
                </Button>
              }
            />
          )}
          {can('invoice.read') && (
            <DocumentRow
              icon={Receipt}
              title={tr('ipd', 'statement')}
              hint={tr('ipd', 'ws_statement_hint')}
              action={
                <Link to={`/billing/statement?admissionId=${admission.id}`}>
                  <Button size="sm" variant="outline">
                    {tr('ipd', 'ws_open')}
                  </Button>
                </Link>
              }
            />
          )}
          <DocumentRow
            icon={FolderOpen}
            title={tr('ipd', 'full_record')}
            hint={tr('ipd', 'ws_full_record_hint')}
            action={
              <Link to={`/patients/${admission.patient.id}`}>
                <Button size="sm" variant="outline">
                  {tr('ipd', 'ws_open')}
                </Button>
              </Link>
            }
          />
        </ul>
      </ShellPanel>
    </>
  );
}
